import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface MunicipalityOption {
  value: string;
  label: string;
  description: string;
  name: string;
  state: string;
  nfseEnabled: boolean;
  status: string;
}

interface MunicipalitySearchResponse {
  options: MunicipalityOption[];
  totalCount: number;
}

const DEBOUNCE_MS = 300;

function useDebouncedValue<T>(value: T, delay = DEBOUNCE_MS): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

async function fetchMunicipalities(
  query: string,
  state?: string | null,
): Promise<MunicipalitySearchResponse> {
  const { data, error } = await supabase.functions.invoke<MunicipalitySearchResponse>("fiscal-reference-data", {
    body: {
      type: "municipality",
      q: query,
      state: state || undefined,
    },
  });

  if (error) throw error;

  return data ?? { options: [], totalCount: 0 };
}

export function useFocusMunicipalitySearch({
  query,
  state,
  enabled = true,
}: {
  query: string;
  state?: string | null;
  enabled?: boolean;
}) {
  const debouncedQuery = useDebouncedValue(query);
  const trimmedQuery = debouncedQuery.trim();
  const shouldSearch = enabled && (trimmedQuery.length >= 2 || /^\d{7}$/.test(trimmedQuery));

  return useQuery<MunicipalitySearchResponse>({
    queryKey: ["focus-municipalities", state ?? "", trimmedQuery],
    enabled: shouldSearch,
    staleTime: 1000 * 60 * 10,
    queryFn: () => fetchMunicipalities(trimmedQuery, state),
  });
}
